import { MINER_STATUSES_E } from '@constants/MinerStatuses';
import { BackRackItemToUnrack_RequestTypes, GetUnrackList_RequestTypes } from '@mobx/api/locations';
import apiService, { createStore, RequestControl, withLoading } from '@mobx/services/http';
import { UserRole } from '@services/http/responses/AccountResponse';
import { IWorkEquipment } from '@store/Equipments/actionTypes';
import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import mainLocationsStore from '.';
import { IItemStore, IRackStore, ItemStore, IUnallocatedMinersStore, IUnallocatedMinersStoreBase, UnallocatedMinersStore } from './interfaces';
import createItemStore from './item';
import { IDragRackItemData3, IRack, IRackItem3, IRackSection3, ISomeItem, IUnrackItem3, TypePartialRackItem3 } from './types';

const createDummyRackStore_ = (): IRackStore => ({
  isLoading: false,
  isReduced: false,
  rack: {} as IRack,
  isDeletable: false,

  async mergeMinerCells(draggedItem: IRackItem3, targetItemId: number, itemIndex: number, draggedItemIndex: number) { return false },
  async unmergeMinerCells(minerId: number) { return false },
  async placeAllocatedRackItem(index: number, item: IRackItem3, data: IDragRackItemData3) {},
  removeItem(id: number) {},
  async placeUnallocatedRackItem(target: IItemStore, data: IUnrackItem3) { return false },
  async addItemEquipment(askUser: boolean, itemId: number, itemIndex: number, rotateEq: IWorkEquipment) { return false },
  setItem(index: number, data: TypePartialRackItem3) {},
  setRackNum(num: string) {},
  setRack(r: IRack) {},
  setReduced(b: boolean) {},
  showRackModalForEdit() {},
  async editRack(sections: IRackSection3[], width: number, height: number) {},
  createItemStores() {},
  deleteItemStores() {},
  async updateRackBLI(bli: number) { return false },
})

const createUnallocatedMinersStore_ = (pageSize: number) => (rc: RequestControl): IUnallocatedMinersStore => {
  const getUnrackList = apiService.createRequest(GetUnrackList_RequestTypes);
  const putRackItemBackToUnrack = apiService.createRequest(BackRackItemToUnrack_RequestTypes);

  let offset_ = 0;

  const self: IUnallocatedMinersStoreBase = {
    isLoading: false,
    isMinersStoreValid: true,
    searchString: undefined as string | undefined,
    statusFilter: undefined as MINER_STATUSES_E | undefined,
    total: 0,
    miners: [] as IUnrackItem3[],

    async search(searchString?: string, status?: MINER_STATUSES_E) {
      offset_ = 0;
      self.searchString = searchString;
      self.statusFilter = status;
      const r = await withLoading(
        self, 
        getUnrackList(rc.createRequestHandle(), pageSize, offset_, self.searchString, self.statusFilter),
      );
      if (!r) return;
      
      runInAction(() => {
        self.total = r.Total;
        self.miners = r.Records;
        self.isMinersStoreValid = true;
      });
    },

    async fetchMore() {
      const offs = self.isMinersStoreValid ? offset_ : 0;
      const lim = self.isMinersStoreValid ? pageSize : offset_ + pageSize;
      const r = await withLoading(
        self, 
        getUnrackList(rc.createRequestHandle(), lim, offs, self.searchString, self.statusFilter),
      );
      if (!r) return -1;

      offset_ += pageSize;
      self.isMinersStoreValid = true;

      runInAction(() => {
        self.total = r.Total;
        self.miners = [...self.miners, ...r.Records];
      });

      return offs;
    },

    reload() {
      return self.search(self.searchString, self.statusFilter);
    },

    // Удаление майнера по id
    removeMiner(id: number) {
      self.miners = self.miners.filter(m => m.id !== id);
      self.isMinersStoreValid = false;
    },

    // Добавление майнера
    addMiner(item: IUnrackItem3) {
      self.miners = [item, ...self.miners];
      self.isMinersStoreValid = false;
    },

    // Изменение порядка элементов
    reorderItems(placedId: number, draggedId: number) {
      const overIndex = self.miners.findIndex(item => item.id === placedId);
      const draggedIndex = self.miners.findIndex(item => item.id === draggedId);

      if (overIndex === -1 || draggedIndex === -1) {
        return;
      }

      const items = [...self.miners];
      const [draggedItem] = items.splice(draggedIndex, 1);
      items.splice(overIndex, 0, draggedItem);
      self.miners = items;
    },

    // Установка цвета майнеров пользователя
    setMinerColor(userId: number, color: string) {
      self.miners = self.miners.map(miner =>
        miner.user_id === userId ? { ...miner, user_color: color } : miner
      );
    },

    invalidateMinersStore() {
      self.isMinersStoreValid = false;
    },

    createItemStore(item: ISomeItem, index: number): ItemStore {
      const dummyRackStore = createStore(createDummyRackStore_);
      return createItemStore(item, index, mainLocationsStore, dummyRackStore);
    },

    async dropItem(item: IDragRackItemData3) {
      const r = await withLoading(self, putRackItemBackToUnrack(rc.createRequestHandle(), item.id));
      if (!r) return false;
      self.addMiner({...item, id: item.miner_id})
      return true;
    },

    async init(role?: UserRole) {
      if (role !== UserRole.Admin) {
        self.isMinersStoreValid = true;
        self.miners = [];
        self.total = 0;
        return;
      }
      const res = await withLoading(self, getUnrackList(rc.createRequestHandle(), pageSize, offset_));
      if (!res) return;
      self.isMinersStoreValid = true;
      self.miners = res.Records;
      self.total = res.Total;
    },
  };

  const r = makeObservable(self, {
    isLoading: observable,
    isMinersStoreValid: observable,
    searchString: observable,
    statusFilter: observable,
    total: observable,
    miners: observable,

    search: action,
    fetchMore: action,
    reload: action,
    removeMiner: action,
    addMiner: action,
    reorderItems: action,
    setMinerColor: action,
    invalidateMinersStore: action,
    createItemStore: action,
    dropItem: action,
  });

  reaction(() => r.isMinersStoreValid, (valid) => !valid && r.reload());

  return r;
}

const createUnallocatedMinersStore = (pageSize: number) => createStore(createUnallocatedMinersStore_(pageSize));
const unallocatedMinersStore: UnallocatedMinersStore = createUnallocatedMinersStore(100);
export default unallocatedMinersStore;
